import { FunctionComponent, MouseEvent, ReactNode, useCallback, useState } from "react";
import { Text } from "components/text";
import { Column } from "components/structure";
import { Button } from "components/buttons";
import { IconButton } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { IProjectError, IPstErrorInfo } from "data/types";
import styles from "./ErrorCard.module.css";

/** A labeled field within an error's expanded details. */
const Field: FunctionComponent<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div>
    <Text size="small" color="muted">
      {label}
    </Text>
    {children}
  </div>
);

interface ErrorDetailsProps {
  entry: IProjectError<any>;
  onRetry: (errorId: string) => Promise<void>;
}

const PstErrorInfoDetails: FunctionComponent<ErrorDetailsProps> = ({ entry, onRetry }) => {
  const info = entry.info as IPstErrorInfo;
  const [retrying, setRetrying] = useState(false);

  const handleRetryClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      setRetrying(true);
      try {
        await onRetry(entry.id);
      } finally {
        setRetrying(false);
      }
    },
    [entry.id, onRetry]
  );

  return (
    <Column style={{ gap: "0.5em" }}>
      <Field label="Form ID">
        <Text>{info.formId}</Text>
      </Field>
      <Field label="Target URL">
        <Text>{info.targetUrl}</Text>
      </Field>
      <Field label="Status">
        <Text>{info.status}</Text>
      </Field>
      <Field label="Response">
        <pre className={styles.codeBlock}>{info.response}</pre>
      </Field>
      <div>
        <Button onClick={handleRetryClick} disabled={retrying}>
          {retrying ? "Resending..." : "Resend"}
        </Button>
      </div>
    </Column>
  );
};

const DefaultErrorInfoDetails: FunctionComponent<ErrorDetailsProps> = ({ entry }) => (
  <pre className={styles.codeBlock}>{JSON.stringify(entry.info, null, 2)}</pre>
);

/** Maps a known error `type` to a component that nicely formats its `info` (and, for
 * types that support it, offers actions like retrying). Unknown types fall back to a
 * generic JSON dump via DefaultErrorInfoDetails. */
const ERROR_INFO_DETAILS: Record<string, FunctionComponent<ErrorDetailsProps>> = {
  pst: PstErrorInfoDetails,
};

const ErrorInfoDetails: FunctionComponent<ErrorDetailsProps> = (props) => {
  const Details = ERROR_INFO_DETAILS[props.entry.type] ?? DefaultErrorInfoDetails;
  return <Details {...props} />;
};

/** Maps a known error `type` to a human-readable label. Unknown types fall back to
 * the raw type value. */
const ERROR_TYPE_LABELS: Record<string, string> = {
  pst: "Post Submit Target Error",
};

const errorTypeLabel = (type: string): string => ERROR_TYPE_LABELS[type] ?? type;

interface ErrorCardProps {
  entry: IProjectError;
  onClear: (errorId: string) => void;
  onRetry: (errorId: string) => Promise<void>;
}

/** A single, self-contained error entry: a summary row (time, classification, clear
 * button) that expands on click to show type-specific details. */
export const ErrorCard: FunctionComponent<ErrorCardProps> = ({ entry, onClear, onRetry }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => setIsExpanded((current) => !current), []);

  return (
    <div className={styles.errorCard} onClick={toggleExpanded}>
      <div className={styles.errorSummary}>
        <div className={styles.errorSummaryText}>
          <Text>{new Date(entry.ts).toLocaleString()}</Text>
          <Text>{errorTypeLabel(entry.type)}</Text>
        </div>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClear(entry.id);
          }}
          aria-label="Clear error"
        >
          <Delete fontSize="small" />
        </IconButton>
      </div>
      {isExpanded && (
        <div className={styles.errorDetails}>
          <ErrorInfoDetails entry={entry} onRetry={onRetry} />
        </div>
      )}
    </div>
  );
};
